'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import {
  InOutMoneyDto,
  PetrohqApiResponseListInOutMoneyDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function Money() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [moneyData, setMoneyData] = useState<InOutMoneyDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 입출금자료 데이터 가져오기 함수
  const fetchMoneyData = useCallback(async () => {
    setIsLoading(true);
    try {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('money', storeCodeList);

      const response = await apiClient.get('inout/money', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListInOutMoneyDto;

      if (data.result) {
        setMoneyData(data.result);
      }
    } catch (error) {
      console.error('Money data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '입출금자료 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchMoneyData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchMoneyData();
  }, []);

  // 엑셀 내보내기
  const handleExcelExport = () => {
    toast({
      title: '준비 중',
      description: '엑셀 내보내기 기능을 준비하고 있습니다',
    });
  };


  return (
    <div className='h-full'>
      <ExpandableCard
        title='입출금자료 상세'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('money')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
            actions={
              <Button variant='outline' size='sm' onClick={handleExcelExport}>
                <Download className='h-4 w-4 mr-2' />
                엑셀
              </Button>
            }
          />
        }
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: false },
              }}
              rows={moneyData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'junGubun'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'junGye'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '매장' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '영업일' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'junGubun'}
                header={{ text: '입/출' }}
                width={60}
              />
              <RGDataColumn
                fieldName={'junGye'}
                header={{ text: '계정과목(내역)' }}
                width={150}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '금액' }}
                width={120}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {moneyData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
